import React, { useEffect, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { Collapse } from "react-collapse";
import "./FireContour.css";
import { Dropdown, DropdownButton } from "react-bootstrap";
import * as turf from "@turf/turf";
import { ConfigProvider, TimePicker } from "antd";
import frFR from "antd/es/locale/fr_FR";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import DatePicker from "react-datepicker";
import { registerLocale } from "react-datepicker";
import fr from "date-fns/locale/fr";
import "react-datepicker/dist/react-datepicker.css";
import { parseContourFile } from "../../../utils/drawTools/ImportContourUtils";
import { TIME_STEPS } from "../../../constants/defaults";
import SimulationModal from "../simulationModal/SimulationModal";
import { useAccessRights } from "../../../hooks/userPermissions/useAccessRights";

registerLocale("fr", fr);
dayjs.locale("fr");

const FireContour = ({
  openSection,
  toggleSecondSection,
  handleSendPolygons,
  windSpeed,
  windDirection,
  fuelMoisture,
  temperatureParam,
  handleTemperatureChange,
  handleWindSpeedChange,
  handleWindDirectionChange,
  handleFuelMoistureChange,
  selectedMeteoGetOption,
  onChangeSelectedMeteoOption,
  onShowPregressBar,
  contourHour,
  setContourHour,
  contourDate,
  setContourDate,
  getPolygons,
  getPoints,
  triggerTooltip,
  currentTimeZone,
  handleReset,
  toggleFirstSection,
  geoJsonResults,
  originFireHour,
  validateSecondSection,
  originFireRef,
  originFireDate,
  handleResetOpacity,
  drawRef,
  mapRef,
  predictionHours,
  setPredictionHours,
  timeStep,
  setTimeStep,
  isLightMode,
  resetValidatedSections,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [showReprojTooltip, setShowReprojTooltip] = useState(false);
  // ── Import state ─────────────────────────────────────────────────────────────
  const [importError, setImportError] = useState("");
  // object = { name, wasReprojected, detectedEpsg }  after a successful import
  const [importedFileInfo, setImportedFileInfo] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef(null);

  // ── Access permissions ─────────────────────────
  const { canAccessLayer } = useAccessRights();
  const isDynamicAllowed = canAccessLayer("physical_model", "dynamic");
  const isSimulationAllowed = canAccessLayer("physical_model", "simulation");
  useEffect(() => {
    if (
      selectedMeteoGetOption === "Réelles par satellite" &&
      !isDynamicAllowed &&
      isSimulationAllowed
    ) {
      onChangeSelectedMeteoOption("Ajuster les données météo");
      setTimeStep("1h"); // simulation mode only supports 1h
    } else if (
      selectedMeteoGetOption === "Ajuster les données météo" &&
      !isSimulationAllowed &&
      isDynamicAllowed
    ) {
      onChangeSelectedMeteoOption("Réelles par satellite");
    }
  }, [
    isDynamicAllowed,
    isSimulationAllowed,
    selectedMeteoGetOption,
    onChangeSelectedMeteoOption,
    setTimeStep,
  ]);

  // ── Date helpers ─────────────────────────────────────────────────────────────
  const currentDate = new Date();
  const minDate = new Date(currentDate.getFullYear(), 6, 1); // month index 6 = July
  const maxDate = new Date(currentDate);


  const isPredictionReady = geoJsonResults.some((r) => r?.contour);

  // ── Geometry helpers ─────────────────────────────────────────────────────────
  const isValidCoordinate = (coordinate) =>
    Array.isArray(coordinate) &&
    coordinate.length === 2 &&
    !isNaN(coordinate[0]) &&
    !isNaN(coordinate[1]);

 /*Returns points inside or on the boundary of drawn polygons.
 * Uses small (~5 m) and larger (~50 m) buffers to handle edge points
 * and minor coordinate shifts in imported, normalized polygons.
 */
  const checkPointsInsidePolygons = (polygons, points) => {
    const result = [];
    points.forEach((point) => {
      const coordinates = point.geometry.coordinates;
      if (!isValidCoordinate(coordinates)) return;

      const inside = polygons.some((polygon) => {
        // Pass 1 — standard check, boundary included
        if (
          turf.booleanPointInPolygon(point, polygon, { ignoreBoundary: false })
        ) {
          return true;
        }
        // Pass 2 — tiny buffer (~5 m) to catch snapped-to-edge points
        try {
          const buffered5m = turf.buffer(polygon, 0.005, {
            units: "kilometers",
          });
          if (
            turf.booleanPointInPolygon(point, buffered5m, {
              ignoreBoundary: false,
            })
          ) {
            return true;
          }
        } catch {
          // ignore buffer errors, fall through to pass 3
        }
       // Pass 3 — apply a ~50 m buffer to account for coordinate rounding
// during polygon normalization and prevent edge markers from falling outside.
        try {
          const buffered50m = turf.buffer(polygon, 0.05, {
            units: "kilometers",
          });
          return turf.booleanPointInPolygon(point, buffered50m, {
            ignoreBoundary: false,
          });
        } catch {
          return false;
        }
      });

      if (inside) result.push(point);
    });
    return result;
  };

  // ── Form submission ───────────────────────────────────────────────────────────
  const handleFormSubmit = (e) => {
    e.preventDefault();
    const polygons = getPolygons();
    const points = getPoints();
    handleResetOpacity();

    if (!polygons || polygons.length === 0) {
      triggerTooltip("Veuillez d'abord tracer ou importer un contour!");
      return;
    }
    if (polygons.length > 1) {
      triggerTooltip(
        "Vous devez avoir un seul polygone sur la carte pour lancer l'opération. Veuillez en supprimer.",
      );
      return;
    }
    if (!contourHour) {
      triggerTooltip("Veuillez d'abord préciser l'heure exacte du contour!");
      return;
    }
    if (!contourDate) {
      triggerTooltip("Veuillez d'abord préciser la date exacte du contour!");
      return;
    }
    const validPoints = checkPointsInsidePolygons(polygons, points);
    if (validPoints.length === 0) {
      triggerTooltip(
        "Le contour principal ne part pas du point d'origine du feu.",
      );
      return;
    }
    if (contourDate === originFireDate && contourHour < originFireHour) {
      triggerTooltip(
        "Erreur: L'heure du contour doit être après l'heure d'origine du feu!",
      );
      return;
    }
    if (contourDate < originFireDate) {
      triggerTooltip(
        "Erreur: La date du contour ne doit être avant la date d'origine du feu!",
      );
      return;
    }
    if (!originFireRef) {
      triggerTooltip("Veuillez d'abord taper la référence du feu!");
      return;
    }
    if (!originFireDate) {
      triggerTooltip("Veuillez d'abord entrer la date de la première alerte!");
      return;
    }
    if (!originFireHour) {
      triggerTooltip("Veuillez d'abord entrer l'heure de la première alerte!");
      return;
    }

    toggleSecondSection();
    validateSecondSection();
    onShowPregressBar(true);
    handleSendPolygons(currentTimeZone);
  };

  const onChangeContourHour = (time) => {
    if (time) setContourHour(time.format("HH:mm"));
  };

  // ── Reset ─────────────────────────────────────────────────────────────────────
  const reinitializeOperation = () => {
    handleReset();
    toggleFirstSection();
    clearImport();
    onShowPregressBar(false);
    resetValidatedSections();
  };

  // ── Météo / time-step selection ───────────────────────────────────────────────
  const lastRealStepRef = useRef(timeStep);

  useEffect(() => {
    if (selectedMeteoGetOption !== "Ajuster les données météo") {
      lastRealStepRef.current = timeStep;
    }
  }, [timeStep, selectedMeteoGetOption]);

  const handleSelect = (eventKey) => {
    if (eventKey === "Ajuster les données météo") {
      if (!isSimulationAllowed) return;
      if (selectedMeteoGetOption !== "Ajuster les données météo") {
        lastRealStepRef.current = timeStep;
      }
      setTimeStep("1h");
    } else {
      if (!isDynamicAllowed) return;
      setTimeStep(lastRealStepRef.current || "30min");
    }
    onChangeSelectedMeteoOption(eventKey);
  };

  // ── Simulation modal ──────────────────────────────────────────────────────────
  const handleOpenModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

  const handleInputChange = (e, rowKey, index) => {
    let newValue = e.target.value;

    if (newValue === "" || newValue === "-") {
      if (rowKey === "temperature") handleTemperatureChange(index, newValue);
      return;
    }
    if (!/^-?\d*\.?\d*$/.test(newValue)) return;

    const parsed = Number(newValue);
    if (isNaN(parsed)) return;

    const limits = {
      windSpeed: { min: 0, max: 100 },
      windDirection: { min: 0, max: 360 },
      fuelMoisture: { min: 1, max: 40 },
      temperature: { min: -20, max: 60 },
    };

    const { min, max } = limits[rowKey];
    const clamped = Math.min(Math.max(parsed, min), max);

    if (rowKey === "windSpeed") handleWindSpeedChange(index, clamped);
    else if (rowKey === "windDirection")
      handleWindDirectionChange(index, clamped);
    else if (rowKey === "fuelMoisture")
      handleFuelMoistureChange(index, clamped);
    else if (rowKey === "temperature") handleTemperatureChange(index, clamped);
  };

  const handleContourDateChange = (date) => {
    if (date) setContourDate(date.toISOString().split("T")[0]);
  };

  // ── Import helpers ────────────────────────────────────────────────────────────

  /**
   * Remove the imported polygon from Mapbox Draw and reset all import state.
   * Fire-origin point markers are intentionally left untouched.
   */
  const clearImport = () => {
    setImportError("");
    setImportedFileInfo(null);

    if (!drawRef?.current) return;

    const polygonIds = drawRef.current
      .getAll()
      .features.filter((f) => f.geometry.type === "Polygon")
      .map((f) => f.id);

    if (polygonIds.length) {
      drawRef.current.delete(polygonIds);
    }
  };

  /* Handle file input change.
 * Parser converts all geometries to EPSG:4326 and normalizes them
 * for compatibility with Mapbox Draw and the backend.
 */
  const handleFileImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    setImportError("");
    setImportedFileInfo(null);
    setIsImporting(true);

    try {
      const { features, wasReprojected, detectedEpsg } =
        await parseContourFile(file);

      if (!drawRef?.current) {
        throw new Error("L'outil de dessin n'est pas encore initialisé.");
      }

      const addedIds = drawRef.current.add({
        type: "FeatureCollection",
        features,
      }); // returns an array of ids, one per feature

      if (mapRef?.current) {
        const combined = { type: "FeatureCollection", features };
        const bbox = turf.bbox(combined);
        mapRef.current.fitBounds(
          [
            [bbox[0], bbox[1]],
            [bbox[2], bbox[3]],
          ],
          { padding: 60, duration: 800 },
        );
      }

      setImportedFileInfo({
        name: file.name,
        wasReprojected,
        detectedEpsg,
        ids: addedIds, // track ALL of them, as we set up earlier
        count: features.length,
      });
    } catch (err) {
      setImportError(err.message);
    } finally {
      setIsImporting(false);
    }
  };
  const triggerFileInput = () => {
    setImportError("");
    fileInputRef.current?.click();
  };

  return (
    <div>
      <Collapse isOpened={openSection === "contour"}>
        <Form className="form" onSubmit={handleFormSubmit} autoComplete="off">
          {/* ── Date ── */}
          <Form.Group className="form-group-custom">
            <Form.Label>
              Date du contour initial
              <b style={{ color: "red", fontSize: "16px" }}>&nbsp;*</b>
            </Form.Label>
            <DatePicker
              selected={originFireDate}
              locale="fr"
              className="date-input"
              dateFormat="dd/MM/yyyy"
              value={contourDate}
              onChange={handleContourDateChange}
              minDate={minDate}
              maxDate={maxDate}
              placeholder="Sélectionnez la date"
              data-testid="fire-contour-date-input"
            />
          </Form.Group>

          {/* ── Hour ── */}
          <Form.Group className="form-group-custom">
            <Form.Label>
              Précisez l'heure exacte de contour
              <b style={{ color: "red", fontSize: "16px" }}>&nbsp;*</b>
            </Form.Label>
            <ConfigProvider locale={frFR}>
              <TimePicker
                className="time-input"
                style={{ width: "100%", height: "30px" }}
                format="HH:mm"
                placeholder="Sélectionnez l'heure"
                value={contourHour ? dayjs(contourHour, "HH:mm") : null}
                onSelect={onChangeContourHour}
                data-testid="fire-contour-time-input"
              />
            </ConfigProvider>
          </Form.Group>

          {/* ── Météo ── */}
          <div className="form-group-custom">
            <Form.Label>
              Données Météo
              <b style={{ color: "red", fontSize: "16px", padding: "0px" }}>
                &nbsp;*
              </b>
            </Form.Label>
            <DropdownButton
              id="weather-dropdown"
              title={selectedMeteoGetOption}
              onSelect={handleSelect}
              data-testid="fire-contour-weather-dropdown"
            >
              <Dropdown.Item
                eventKey="Réelles par satellite"
                disabled={!isDynamicAllowed}
              >
                Réelles par satellite
              </Dropdown.Item>
              <Dropdown.Item
                eventKey="Ajuster les données météo"
                onClick={handleOpenModal}
                disabled={!isSimulationAllowed}
              >
                Simuler les données météo
              </Dropdown.Item>
            </DropdownButton>
          </div>

          {/* ── Prediction duration ── */}
          <div className="form-group-custom prediction-params-group">
            <Form.Label className="prediction-params-label">
              Durée de prédiction (en heures)
              <b style={{ color: "red", fontSize: "16px" }}>&nbsp;*</b>
            </Form.Label>
            <div className="polygon-stepper">
              <input
                type="range"
                className="polygon-slider"
                min={1}
                max={12}
                value={predictionHours}
                style={{ "--fill": `${((predictionHours - 1) / 11) * 100}%` }}
                onChange={(e) => setPredictionHours(Number(e.target.value))}
                data-testid="fire-contour-prediction-slider"
              />
              <span className="stepper-value">{predictionHours}</span>
            </div>
          </div>

          {/* ── Time step ── */}
          <div className="form-group-custom prediction-params-group">
            <Form.Label className="prediction-params-label">
              Intervalles de temps
              <b style={{ color: "red", fontSize: "16px" }}>&nbsp;*</b>
            </Form.Label>
            <div className="timestep-pills">
              {TIME_STEPS.map(({ label, value }) => {
                const isSimulation =
                  selectedMeteoGetOption === "Ajuster les données météo";
                const disabled = isSimulation && value !== "1h";
                return (
                  <button
                    type="button"
                    key={value}
                    className={`timestep-pill ${timeStep === value ? "selected" : ""} ${disabled ? "disabled" : ""}`}
                    onClick={() => !disabled && setTimeStep(value)}
                    disabled={disabled}
                    title={
                      disabled
                        ? "Seul le pas de 1h est disponible en mode simulation"
                        : ""
                    }
                    data-testid={`fire-contour-timestep`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Contour input section ── */}
          <div className="contour-input-section">
            <p className="note-contour">
              Dessinez le contour actuel du feu sur la carte,
              {/* ou importez-le depuis un fichier. */}
            </p>

            {/* Hidden file input */}
            {/* <input
              ref={fileInputRef}
              type="file"
              accept=".geojson,.json,.kml"
              style={{ display: "none" }}
              onChange={handleFileImport}
            /> */}

            {/*
              Import button — visible only when no file is imported yet.
              Once a file is imported the badge below takes its place.
            */}
            {/* {!importedFileInfo && (
              <Button
                type="button"
                variant="outline-secondary"
                className="import-contour-btn"
                onClick={triggerFileInput}
                disabled={isImporting}
                data-testid="fire-contour-import-button"
              >
                {isImporting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    Importation…
                  </>
                ) : (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ marginRight: "6px", verticalAlign: "middle" }}
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    Importer un contour (GeoJSON / KML)
                  </>
                )}
              </Button>
            )} */}

            {importedFileInfo && (
              <div
                className={`import-file-badge ${importedFileInfo.wasReprojected ? "import-file-badge--has-tooltip" : ""}`}
                onMouseEnter={() =>
                  importedFileInfo.wasReprojected && setShowReprojTooltip(true)
                }
                onMouseLeave={() => setShowReprojTooltip(false)}
              >
                <div className="import-file-badge__info">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="import-file-badge__check"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span className="import-file-badge__name">
                    {importedFileInfo.name}
                  </span>
                </div>

                <div className="import-file-badge__actions">
                  {/* {importedFileInfo.wasReprojected && (
                    <span className="import-file-badge__crs-tag">ⓘ</span>
                  )} */}
                  <button
                    type="button"
                    className="import-file-badge__remove"
                    onClick={clearImport}
                    title="Supprimer le contour importé"
                    aria-label="Supprimer le contour importé"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
                {/* Tooltip shown on hover */}
                {showReprojTooltip && (
                  <div className="import-reproj-tooltip">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ flexShrink: 0, marginTop: "1px" }}
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                    <div>
                      <div>
                        Projection&nbsp;:{" "}
                        <strong>{importedFileInfo.detectedEpsg}</strong>
                      </div>
                      <div>Reprojeté en WGS&nbsp;84 (EPSG:4326)</div>
                    </div>
                  </div>
                )}
              </div>
            )}
            {importError && (
              <div className="import-feedback import-feedback--error">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ marginRight: "5px", flexShrink: 0 }}
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                {importError}
              </div>
            )}
          </div>

          {/* ── Simulation modal ── */}
          <SimulationModal
            show={showModal}
            onClose={handleCloseModal}
            predictionHours={predictionHours}
            windSpeed={windSpeed}
            windDirection={windDirection}
            fuelMoisture={fuelMoisture}
            temperatureParam={temperatureParam}
            onInputChange={handleInputChange}
            isLightMode={isLightMode}
          />

          <Button
            type="submit"
            className="event-btn sidebar-primary-btn"
            style={{ marginTop: "10px" }}
            data-testid="fire-contour-submit-button"
          >
            {isPredictionReady
              ? "Relancer les prédictions"
              : "Lancer les prédictions"}
          </Button>

          <Button
            variant="secondary"
            onClick={reinitializeOperation}
            className="event-btn sidebar-secondary-btn"
            disabled={!isPredictionReady}
            data-testid="fire-contour-reset-button"
          >
            Réinitialiser
          </Button>
        </Form>
      </Collapse>
    </div>
  );
};

export default React.memo(FireContour);
